import { supabase } from "./supabaseClient";

export const getEmptyBillingOverview = () => ({
  subscriptions: [],
  payments: [],
  coinLedger: [],
  entitlements: [],
});

const isFuture = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
};

const accessHasTimeRemaining = (value) => !value || isFuture(value);

const productLineLabel = (line) =>
  String(line || "other")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
const institutionPlanName = (product) => {
  if (product?.product_line !== "institutions") return product?.name;
  const tier = String(product?.metadata?.tier || "");
  const segment = ["friends", "crew", "club"].includes(tier)
    ? "Social"
    : ["team", "startup", "growth", "scale", "enterprise"].includes(tier)
      ? "Business"
      : "";
  return [segment, product?.name].filter(Boolean).join(" ") || product?.name;
};

export const loadBillingOverview = async (profileId) => {
  if (!profileId) return getEmptyBillingOverview();

  const [
    subscriptionsResult,
    paymentsResult,
    coinLedgerResult,
    entitlementsResult,
  ] = await Promise.all([
    supabase
      .from("billing_subscriptions")
      .select("*, billing_products(product_key,name,product_line,metadata), billing_prices(price_key,amount_cents,currency,interval,coin_amount,metadata)")
      .eq("profile_id", profileId)
      .order("created_at", { ascending: false }),
    supabase
      .from("billing_payments")
      .select("*, billing_products(product_key,name,product_line,metadata), billing_prices(price_key,amount_cents,currency,interval,coin_amount,metadata)")
      .eq("profile_id", profileId)
      .order("created_at", { ascending: false })
      .limit(100),
    supabase
      .from("coin_ledger")
      .select("*, billing_products(product_key,name,product_line,metadata), billing_prices(price_key,amount_cents,currency,interval,coin_amount,metadata), billing_payments(id,transaction_id,provider,provider_payment_id)")
      .eq("profile_id", profileId)
      .order("created_at", { ascending: false })
      .limit(100),
    supabase
      .from("customer_entitlements")
      .select("*, entitlement_definitions(entitlement_key,name,product_line), billing_products(product_key,name,product_line,metadata), billing_prices(price_key,amount_cents,currency,interval,coin_amount,metadata)")
      .eq("profile_id", profileId)
      .in("status", ["active", "trialing", "pending"]),
  ]);

  if (subscriptionsResult.error) throw subscriptionsResult.error;
  if (paymentsResult.error) throw paymentsResult.error;
  if (coinLedgerResult.error) throw coinLedgerResult.error;
  if (entitlementsResult.error) throw entitlementsResult.error;

  return {
    subscriptions: subscriptionsResult.data || [],
    payments: paymentsResult.data || [],
    coinLedger: coinLedgerResult.data || [],
    entitlements: entitlementsResult.data || [],
  };
};

export const getCurrentBillingPlans = (overview) => {
  const plans = [];
  const seenAccess = new Set();

  (overview.subscriptions || []).forEach((row) => {
    const current = ["active", "trialing", "past_due"].includes(row?.status) || isFuture(row?.current_period_end);
    if (!current) return;
    plans.push({
      id: `sub:${row.id}`,
      kind: "subscription",
      name: institutionPlanName(row?.billing_products) || "Subscription",
      productLine: productLineLabel(row?.billing_products?.product_line),
      source: row?.provider === "stripe" ? "Stripe subscription" : row?.provider || "Subscription",
      status: row?.cancel_at_period_end ? "cancels" : row?.status || "active",
      renewsAt: row?.cancel_at_period_end ? null : row?.current_period_end || null,
      expiresAt: row?.cancel_at_period_end ? row?.current_period_end || null : null,
      cancelAtPeriodEnd: !!row?.cancel_at_period_end,
      amountCents: typeof row?.billing_prices?.amount_cents === "number" ? row.billing_prices.amount_cents : null,
      currency: row?.billing_prices?.currency || "eur",
      interval: row?.billing_prices?.interval || null,
      coinAmount: null,
    });
  });

  (overview.entitlements || []).forEach((row) => {
    if (row?.source === "stripe" || !["active", "trialing", "pending"].includes(row?.status)) return;
    if (!accessHasTimeRemaining(row?.expires_at)) return;
    const product = row?.billing_products;
    const key = `${row?.source}:${row?.product_id || product?.product_key || "unknown"}:${row?.price_id || row?.payment_id || row?.subscription_id || "grant"}`;
    if (seenAccess.has(key)) return;
    seenAccess.add(key);
    plans.push({
      id: `access:${key}`,
      kind: "access",
      name: institutionPlanName(product) || row?.entitlement_definitions?.name || "Access grant",
      productLine: productLineLabel(product?.product_line || row?.entitlement_definitions?.product_line),
      source: row?.source === "coins" ? "Coin unlock" : row?.source || "Access",
      status: row?.status || "active",
      renewsAt: null,
      expiresAt: row?.expires_at || null,
      cancelAtPeriodEnd: false,
      amountCents: null,
      currency: row?.billing_prices?.currency || "eur",
      interval: row?.billing_prices?.interval || null,
      coinAmount: typeof row?.billing_prices?.coin_amount === "number" ? row.billing_prices.coin_amount : null,
    });
  });

  return plans;
};
